import { atom, useAtom, useSetAtom } from 'jotai';
import { TStartDate } from 'types';

const keywordFilterAtom = atom<string>('');
keywordFilterAtom.debugLabel = 'keywordFilter';

const addressFilterAtom = atom<string>('');
addressFilterAtom.debugLabel = 'addressFilter';

const addressCoordsAtom = atom<[number, number] | null>(null);
addressCoordsAtom.debugLabel = 'addressCoords';

const regionFilterAtom = atom<string[]>([]);
regionFilterAtom.debugLabel = 'regionFilter';

const stateFilterAtom = atom<string>('');
stateFilterAtom.debugLabel = 'stateFilter';

const cityFilterAtom = atom<number[]>([]);
cityFilterAtom.debugLabel = 'cityFilter';

const docTypeFilterAtom = atom<string[]>([]);
docTypeFilterAtom.debugLabel = 'docTypeFilter';

const dateCreateFilterAtom = atom<TStartDate>('');
dateCreateFilterAtom.debugLabel = 'dateCreateFilter';

const dateUpdatedFilterAtom = atom<TStartDate>('');
dateUpdatedFilterAtom.debugLabel = 'dateUpdatedFilter';

const sortDirectionAtom = atom<boolean>(false);
sortDirectionAtom.debugLabel = 'sortDirectionAtom';

const sortedHeadingAtom = atom<string>('');
sortedHeadingAtom.debugLabel = 'sortedHeadingAtom';

export const useKeywordFilter = () => {
  const [keywordFilter, setKeywordFilter] = useAtom(keywordFilterAtom);

  return { keywordFilter, setKeywordFilter };
};

export const useAddressFilter = () => {
  const [addressFilter, setAddressFilter] = useAtom(addressFilterAtom);

  return { addressFilter, setAddressFilter };
};

export const useAddressCoords = () => {
  const [addressCoords, setAddressCoords] = useAtom(addressCoordsAtom);

  return { addressCoords, setAddressCoords };
};

export const useRegionFilter = () => {
  const [regionFilter, setRegionFilter] = useAtom(regionFilterAtom);

  return {
    regionFilter,
    setRegionFilter,
  };
};

export const useStateDocFilter = () => {
  const [stateFilter, setStateFilter] = useAtom(stateFilterAtom);

  return { stateFilter, setStateFilter };
};

export const useCityDocFilter = () => {
  const [cityFilter, setCityFilter] = useAtom(cityFilterAtom);

  return { cityFilter, setCityFilter };
};

export const useDocTypeFilter = () => {
  const [docTypeFilter, setDocTypeFilter] = useAtom(docTypeFilterAtom);

  return { docTypeFilter, setDocTypeFilter };
};

export const useDateCreatedFilter = () => {
  const [dateCreatedFilter, setDateCreatedFilter] = useAtom(dateCreateFilterAtom);

  return {
    dateCreatedFilter,
    setDateCreatedFilter,
  };
};

export const useDateUpdatedFilter = () => {
  const [dateUpdatedFilter, setDateUpdatedFilter] = useAtom(dateUpdatedFilterAtom);

  return {
    dateUpdatedFilter,
    setDateUpdatedFilter,
  };
};

export const useResetFilters = () => {
  const setKeywordFilter = useSetAtom(keywordFilterAtom);
  const setAddressFilter = useSetAtom(addressFilterAtom);
  const setAddressCoords = useSetAtom(addressCoordsAtom);
  const setRegionFilter = useSetAtom(regionFilterAtom);
  const setDateCreateFilter = useSetAtom(dateCreateFilterAtom);
  const setDateUpdatedFilter = useSetAtom(dateUpdatedFilterAtom);
  const setCityFilter = useSetAtom(cityFilterAtom);
  const setDocTypeFilter = useSetAtom(docTypeFilterAtom);

  const resetFilters = () => {
    setKeywordFilter('');
    setAddressFilter('');
    setAddressCoords(null);
    setRegionFilter([]);
    setDateCreateFilter('');
    setDateUpdatedFilter('');
    setCityFilter([]);
    setDocTypeFilter([]);
  };

  return { resetFilters };
};

export const useDocumentTableHeaderSorting = () => {
  const [isSortDescending, setIsSortDescending] = useAtom(sortDirectionAtom);
  const [sortedHeader, setSortedHeader] = useAtom(sortedHeadingAtom);

  const toggleSortedHeader = (header: string) => {
    if (sortedHeader === header) {
      setIsSortDescending((prev) => !prev);
    } else {
      setSortedHeader(header);
      setIsSortDescending(true); // Default to descending when changing header
    }
  };

  const getCurrentSortStatusAsArray = () => {
    if (!sortedHeader) return [];

    return isSortDescending ? [`-${sortedHeader}`] : [sortedHeader];
  };

  return {
    isSortDescending,
    setIsSortDescending,
    sortedHeader,
    setSortedHeader,
    toggleSortedHeader,
    getCurrentSortStatusAsArray,
  };
};
